import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CircularProgress from "@material-ui/core/CircularProgress";
import { ShieldCheck, Copy, Download, Smartphone, KeyRound } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: 24,
      background: theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(18, 18, 19, 0.97) 0%, rgba(22, 22, 24, 0.97) 100%)"
        : "#FFFFFF",
      border: theme.palette.type === "dark" ? "1px solid rgba(91, 124, 255, 0.2)" : "none",
      boxShadow: theme.palette.type === "dark"
        ? "0 24px 80px rgba(0, 0, 0, 0.6)"
        : "0 24px 80px rgba(0, 0, 0, 0.12)",
      padding: 0,
    },
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    paddingBottom: 12,
    borderBottom: theme.palette.type === "dark" ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.06)",
  },
  qrBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
    padding: 16,
  },
  qr: {
    width: 220,
    height: 220,
    borderRadius: 12,
    background: "#fff",
    padding: 8,
  },
  secret: {
    fontFamily: "monospace",
    fontSize: 14,
    padding: "8px 12px",
    borderRadius: 8,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
    letterSpacing: 2,
    wordBreak: "break-all",
    textAlign: "center",
  },
  codesGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 8,
    margin: "12px 0",
  },
  code: {
    fontFamily: "monospace",
    padding: "10px 12px",
    borderRadius: 8,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
    textAlign: "center",
    fontSize: 14,
    letterSpacing: 1,
  },
  warn: {
    color: theme.palette.type === "dark" ? "#fbbf24" : "#b45309",
    fontSize: 13,
    marginTop: 8,
  },
  rowBtn: {
    display: "flex",
    gap: 8,
    justifyContent: "center",
    marginTop: 8,
  },
}));

const MfaSetupDialog = ({ open, onClose, setupTicket, forceMode = false }) => {
  const classes = useStyles();
  const { finalizeMfaSetupSession } = useContext(AuthContext);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [secret, setSecret] = useState("");
  const [otpauthUrl, setOtpauthUrl] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [otp, setOtp] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [savedAck, setSavedAck] = useState(false);
  const [debugError, setDebugError] = useState("");
  const [pendingSession, setPendingSession] = useState(null);

  useEffect(() => {
    if (!open) {
      setSecret("");
      setOtpauthUrl("");
      setQrDataUrl("");
      setDebugError("");
      return;
    }
    let cancelled = false;
    (async () => {
      setStep(1);
      setLoading(true);
      setDebugError("");
      try {
        const body = setupTicket ? { setupTicket } : {};
        const config = setupTicket
          ? { headers: { Authorization: "" }, _retry: true }
          : {};
        const { data } = await api.post("/auth/mfa/setup", body, config);
        if (cancelled) return;
        if (!data || !data.qrCodeDataUrl) {
          setDebugError("Resposta sem QR code: " + JSON.stringify(data).slice(0, 200));
          return;
        }
        setSecret(data.secret);
        setOtpauthUrl(data.otpauthUrl);
        setQrDataUrl(data.qrCodeDataUrl);
      } catch (err) {
        const msg = err?.response?.data?.error || err?.message || String(err);
        setDebugError(`Falha ao iniciar MFA: ${msg}`);
        toastError(err);
        if (!forceMode) onClose && onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, setupTicket]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirm = async () => {
    if (otp.length < 6) {
      toast.error(i18n.t("mfa.errors.invalidOtp"));
      return;
    }
    setLoading(true);
    try {
      const body = setupTicket ? { otp, setupTicket } : { otp };
      const config = setupTicket
        ? { headers: { Authorization: "" }, _retry: true }
        : {};
      const { data } = await api.post("/auth/mfa/setup/confirm", body, config);
      setBackupCodes(data.backupCodes || []);
      if (data.token && data.user && setupTicket) {
        setPendingSession(data);
      }
      setStep(3);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const copyAll = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    toast.success(i18n.t("mfa.setup.copied"));
  };

  const download = () => {
    const blob = new Blob([backupCodes.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sttelar-mfa-backup-codes.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFinish = () => {
    if (pendingSession) {
      finalizeMfaSetupSession(pendingSession);
      setPendingSession(null);
      return;
    }
    if (onClose) onClose({ done: true });
  };

  // forceMode: bloqueia ESC e clique no backdrop via filtro de reason no
  // onClose (substitui as props disableEscapeKeyDown/disableBackdropClick
  // que estão deprecated no Material UI 4.x e geravam warning no console).
  const handleClose = (_event, reason) => {
    if (forceMode && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      return;
    }
    if (onClose) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className={classes.dialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle disableTypography className={classes.title}>
        <ShieldCheck size={22} strokeWidth={1.75} />
        <Typography variant="h6">{i18n.t("mfa.setup.title")}</Typography>
      </DialogTitle>
      <DialogContent>
        {loading && step === 1 && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && step === 1 && (
          <Box className={classes.qrBox}>
            <Typography variant="body2" align="center">
              <Smartphone size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />
              {i18n.t("mfa.setup.scanQr")}
            </Typography>
            {debugError && (
              <Typography variant="caption" style={{ color: "#ef4444", textAlign: "center" }}>
                {debugError}
              </Typography>
            )}
            {qrDataUrl ? (
              <img src={qrDataUrl} alt="QR Code" className={classes.qr} />
            ) : !debugError && (
              <Box style={{ width: 220, height: 220, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress size={24} />
              </Box>
            )}
            <Typography variant="caption">{i18n.t("mfa.setup.manualSecret")}</Typography>
            <div className={classes.secret}>{secret || "..."}</div>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setStep(2)}
              disabled={!secret}
            >
              {i18n.t("mfa.setup.next")}
            </Button>
          </Box>
        )}

        {step === 2 && (
          <Box className={classes.qrBox}>
            <KeyRound size={28} strokeWidth={1.5} />
            <Typography variant="body2" align="center">
              {i18n.t("mfa.setup.confirmOtp")}
            </Typography>
            <TextField
              variant="outlined"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputProps={{ inputMode: "numeric", maxLength: 6, style: { textAlign: "center", fontSize: 22, letterSpacing: 8 } }}
              autoFocus
            />
            <div className={classes.rowBtn}>
              <Button onClick={() => setStep(1)} disabled={loading}>
                {i18n.t("mfa.setup.back")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirm}
                disabled={loading || otp.length < 6}
              >
                {loading ? <CircularProgress size={18} /> : i18n.t("mfa.setup.confirm")}
              </Button>
            </div>
          </Box>
        )}

        {step === 3 && (
          <Box p={2}>
            <Typography variant="body1">{i18n.t("mfa.setup.backupTitle")}</Typography>
            <Typography variant="caption" color="textSecondary">
              {i18n.t("mfa.setup.backupHint")}
            </Typography>
            <div className={classes.codesGrid}>
              {backupCodes.map((c) => (
                <div key={c} className={classes.code}>{c}</div>
              ))}
            </div>
            <div className={classes.rowBtn}>
              <Button startIcon={<Copy size={16} />} onClick={copyAll}>
                {i18n.t("mfa.setup.copyAll")}
              </Button>
              <Button startIcon={<Download size={16} />} onClick={download}>
                {i18n.t("mfa.setup.download")}
              </Button>
            </div>
            <FormControlLabel
              control={<Checkbox checked={savedAck} onChange={(e) => setSavedAck(e.target.checked)} />}
              label={i18n.t("mfa.setup.savedAck")}
            />
            <div className={classes.warn}>{i18n.t("mfa.setup.oneTimeWarning")}</div>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!forceMode && step !== 3 && (
          <Button onClick={onClose} disabled={loading}>
            {i18n.t("mfa.setup.cancel")}
          </Button>
        )}
        {step === 3 && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleFinish}
            disabled={!savedAck}
          >
            {i18n.t("mfa.setup.finish")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MfaSetupDialog;
